import store from '@/store';
import Cookies from 'js-cookie';
import Vue from 'vue';
import VueRouter from 'vue-router';

const LoginPage = () => import('@/views/public/login.vue');
const NavigationPage = () => import('@/views/layout/navigation.vue');
const ActivityPage = () => import('@/views/activity/activity.vue');
const DevicesPage = () => import('@/views/devices/devices.vue');
const AuthenticationsPage = () =>
  import('@/views/authentications/authentications.vue');
const UserManagementPage = () =>
  import('@/views/user-management/user-management.vue');
const WorkersPage = () => import('@/views/workers/workers.vue');
const PromptsPage = () => import('@/views/promts/prompts-page.vue');
const ToolsPage = () => import('@/views/tools/tools-page.vue');
const WorkerDetailsPage = () => import('@/views/worker/worker.vue');
const PromptDetailsPage = () => import('@/views/promt/promt.vue');
const ToolDetailsPage = () => import('@/views/promt/tool/tool.vue');
const AutomationPage = () => import('@/views/automation/automation.vue');
const AutomationDetailsPage = () =>
  import('@/views/automation-details/automation-details.vue');
const PatientsPage = () => import('@/views/patients/patients.vue');
const SettingsPage = () => import('@/views/settings-page/settings.vue');
const BrowserAutomationTutorial = () =>
  import('@/views/BrowserAutomationTutorial/BrowserAutomationTutorial.vue');
const AutomationWorkflowBuilder = () =>
  import('@/views/automation-details/builder/Builder.vue');
const AutomationWorkflowHistory = () =>
  import('@/views/automation-details/history/History.vue');
const PatientsDetails = () =>
  import('@/views/patients/popups/patients-popup/patients-popup.vue');
const AutoPayPage = () => import('@/views/autopay/autopay.vue');
const PaymentsPage = () => import('@/views/payments/payments.vue');
const InvoicesDetailsPage = () =>
  import('@/views/payments/patient-details/patient-details.vue');
const WebhooksPage = () => import('@/views/webhooks/webhooks.vue');
const TwoFactor = () => import('@/views/public/two-factor.vue');
const GeneratedCodesPage = () =>
  import('@/views/generated-codes/generated-codes.vue');
const MLModelsPage = () => import('@/views/ml-models/ml-models.vue');
const MLModelPage = () => import('@/views/ml-models/ml-model/ml-model.vue');
const AIChat = () => import('@/views/ai-chat/ai-chat.vue');
const AIWorkspaces = () => import('@/views/ai-workspaces/ai-workspaces.vue');
const CommunicationChannelsSessionsPage = () =>
  import('@/views/ai-workspaces/workspace/communication-channels/communication-channel-sessions/communication-channel-sessions.vue');
const WorkspaceDetails = () =>
  import('@/views/ai-workspaces/workspace/workspace.vue');
const AIChatStartPage = () =>
  import('@/views/ai-chat/start-page/chat-start-page.vue');
const AIChatSessions = () =>
  import('@/views/ai-chat/start-page/sessions-page/sessions.vue');
const ArtifactPage = () =>
  import('@/views/ai-workspaces/workspace/artifacts/artifact-page/artifact-page.vue');
const DataViews = () => import('@/views/data-views/data-views.vue');
const DataViewPage = () =>
  import('@/views/data-views/data-view/data-view-page.vue');
const ClassificationsPage = () =>
  import('@/views/classification-rules/classification-rules-page.vue');
const ClassificationDetailsPage = () =>
  import('@/views/classification-rules/classification-rule-detail.vue');
const ExtractTemplatesPage = () =>
  import('@/views/extract-templates/extract-templates-page.vue');
const ExtractTemplatesDetailsPage = () =>
  import('@/views/extract-templates/extract-template-detail.vue');

class CustomVueRouter extends VueRouter {
  combineQueryStrings(location) {
    const customer = store.state.customer;
    const activeCustomer = customer?.customer?.customer_id;
    const selectedCustomer = customer?.selected_customer?.customer_id;
    const query = {};
    if (activeCustomer === 0) {
      query.customer_id = selectedCustomer;
    }
    return {
      ...location,
      query: {
        ...(location.query || {}),
        ...query,
      },
    };
  }

  push(location, onComplete, onAbort) {
    const newLocation = this.combineQueryStrings(location);
    super.push(newLocation, onComplete, onAbort);
  }

  replace(location, onComplete, onAbort) {
    const newLocation = this.combineQueryStrings(location);
    super.push(newLocation, onComplete, onAbort);
  }
}

Vue.use(CustomVueRouter);

const routes = [
  {
    path: '/login',
    name: 'login',
    component: LoginPage,
  },
  {
    path: '/two-factor',
    name: 'TwoFactor',
    component: TwoFactor,
  },
  {
    path: '/browser-automation-tutorial',
    name: 'BrowserAutomationTutorial',
    component: BrowserAutomationTutorial,
  },
  {
    path: '/',
    component: NavigationPage,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Activity',
        component: ActivityPage,
      },
      {
        path: 'autopay',
        name: 'Autopay',
        component: AutoPayPage,
        meta: { shAdmin: true },
      },
      {
        path: 'generated-codes',
        name: 'Generated Codes',
        component: GeneratedCodesPage,
        meta: { shAdmin: true },
      },
      {
        path: 'workers',
        name: 'Workers',
        component: WorkersPage,
      },
      {
        path: 'workers/:workerId',
        name: 'Worker',
        component: WorkerDetailsPage,
      },
      {
        path: 'models',
        name: 'ML Models',
        component: MLModelsPage,
      },
      {
        path: 'models/:modelId',
        name: 'ML Model',
        component: MLModelPage,
      },
      {
        path: 'prompts/:promptId',
        name: 'Prompt',
        component: PromptDetailsPage,
      },
      {
        path: 'actions/:toolId',
        name: 'Action',
        component: ToolDetailsPage,
      },
      {
        path: 'payments',
        name: 'Payments',
        component: PaymentsPage,
        meta: { paymentV2: true },
      },
      {
        path: 'payments/patients/:patientId',
        name: 'Patient Payments Details',
        component: InvoicesDetailsPage,
      },
      {
        path: 'devices',
        name: 'Devices',
        component: DevicesPage,
      },
      {
        path: 'user-management',
        name: 'User Management',
        component: UserManagementPage,
        meta: { privilegedRoute: true },
      },
      {
        path: 'integrations',
        name: 'Integrations',
        component: AuthenticationsPage,
        meta: { privilegedRoute: true },
      },
      {
        path: 'workflows',
        name: 'Workflows',
        component: AutomationPage,
      },
      {
        path: 'workflows/:automationId/',
        component: AutomationDetailsPage,
        name: 'WorkflowDetails',
        children: [
          {
            path: 'builder',
            name: 'Builder',
            component: AutomationWorkflowBuilder,
          },
          {
            path: 'history',
            name: 'History',
            component: AutomationWorkflowHistory,
          },
          {
            path: '*',
            redirect: '/builder',
          },
        ],
      },
      {
        path: 'patients',
        name: 'Patients',
        component: PatientsPage,
      },
      {
        path: 'patients/:patientId',
        name: 'Patient',
        component: PatientsDetails,
      },
      {
        path: '/settings',
        name: 'Settings',
        component: SettingsPage,
      },
      {
        path: '/webhooks',
        name: 'Webhooks',
        component: WebhooksPage,
      },
      {
        path: 'prompts',
        name: 'prompts',
        component: PromptsPage,
      },
      {
        path: 'actions',
        name: 'Actions',
        component: ToolsPage,
      },
      {
        path: 'classification-rules',
        name: 'ClassificationRules',
        component: ClassificationsPage,
      },
      {
        path: 'classification-rules/:ruleId',
        name: 'ClassificationRule',
        component: ClassificationDetailsPage,
      },
      {
        path: 'extract-templates',
        name: 'ExtractTemplates',
        component: ExtractTemplatesPage,
      },
      {
        path: 'extract-templates/:templateId',
        name: 'ExtractTemplate',
        component: ExtractTemplatesDetailsPage,
      },
      {
        path: '/ai-chat',
        name: 'AI Chat',
        component: AIChat,
      },
      {
        path: '/start-chat',
        name: 'AI Chat',
        component: AIChatStartPage,
      },
      {
        path: '/chat-sessions',
        name: 'AI Chat Sessions',
        component: AIChatSessions,
      },
      {
        path: '/agents',
        name: 'AI Agents',
        component: AIWorkspaces,
      },
      {
        path: '/agents/:workspaceId',
        name: 'AI Agent',
        component: WorkspaceDetails,
      },
      {
        path: '/agents/:workspaceId/communication-channels',
        name: 'Communication Channels Sessions',
        component: CommunicationChannelsSessionsPage,
      },
      {
        path: '/artifacts',
        name: 'AI Artifact',
        component: ArtifactPage,
      },
      {
        path: '/data-views',
        name: 'Data Views',
        component: DataViews,
      },
      {
        path: '/data-views/:dataViewId',
        name: 'Data View',
        component: DataViewPage,
      },
    ],
  },
];

function hasPrivilegedAccess() {
  const user = store.getters['customer/USER'];
  const customer = store.getters['customer/CUSTOMER'];
  const isShAdmin =
    customer?.customer_id === 0 || Cookies.get('customerId') === '0';
  const cookiePrivileged = Cookies.get('is_privileged');
  const isPrivilegedCookie =
    cookiePrivileged === 'true' || cookiePrivileged === true;

  return isShAdmin || !!user?.is_privileged || isPrivilegedCookie;
}

function shouldBlockPrivilegedRoute(to) {
  return (
    to.matched.some((record) => record.meta.privilegedRoute) &&
    !hasPrivilegedAccess()
  );
}

function isShAdmin(to) {
  return (
    to.matched.some((record) => record.meta.shAdmin) &&
    Cookies.get('customerId') !== '0'
  );
}

function isAllowedCustomer({ matched }) {
  const route = matched.find((record) => record.meta.allowedCustomers);
  const customers = route ? route.meta.allowedCustomers : [];

  if (!route) return false;
  if (!store.state.customer) return true;
  const value = !customers.includes(store.state.customer.customer.customer_id);

  return value;
}

function isPaymentV2Customer(to) {
  return (
    to.matched.some((record) => record.meta.paymentV2) &&
    !store.state.customer?.featureFlags?.payment_v2
  );
}

const router = new CustomVueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  const user = store.getters['customer/USER'];

  if (
    to.matched.some((record) => record.meta.requiresAuth) &&
    !Cookies.get('token')
  ) {
    next({ name: 'login' });
    return;
  } else if (user?.forceResetPassword) {
    // If user needs to reset password, only allow access to settings page
    if (to.path !== '/settings') {
      next('/settings');
    } else {
      next();
    }
  } else if (
    isShAdmin(to) ||
    isPaymentV2Customer(to) ||
    shouldBlockPrivilegedRoute(to) ||
    isAllowedCustomer(to)
  ) {
    next({ path: '/' });
  } else {
    next();
  }
});

export default router;
